import React, { Fragment, useState } from 'react';
import { formatCredits } from '../../utils/credits';
import Avatar from '../Avatar';
import OrderHistory from '../OrderHistory';
import OrderStatusBadge from '../OrderStatusBadge';
import './PurchaseHistory.scss';

/**
 * Every purchase made against one order.
 *
 * An order document is an availability for (cinema x version), bought over and
 * over — so the page needs a run of purchases, not one flat event log. Each
 * block is a self-contained record of what was agreed that time round, and
 * expands into its own events.
 *
 * `purchases` comes from the server (bac-server utils/order-purchases.js);
 * deriving it client-side would let the list and the detail page disagree.
 */
const formatDate = value => (value ? new Date(value).toLocaleString() : '—');

const Person = ({ label, user: reference, at }) => {
    if (!reference && !at) return null;

    // Guards an id that was never populated — rendering it would print
    // "undefined undefined" where a name belongs.
    const user = reference?.firstName ? reference : null;

    return (
        <div className="d-flex align-items-center gap-2">
            {user ? (
                <Avatar
                    imageUrl={user.profilePic?.imageUrl}
                    name={`${user.firstName} ${user.lastName}`}
                    size={28}
                />
            ) : (
                <div className="cronjob-avatar d-flex align-items-center justify-content-center">
                    <span className="fas fa-robot" style={{ fontSize: '0.7rem' }} />
                </div>
            )}
            <div>
                <div className="text-white-50" style={{ fontSize: '0.7rem' }}>{label}</div>
                <div className="text-white small">
                    {user ? `${user.firstName} ${user.lastName}` : 'System'}
                    <span className="text-white-50 ms-2" style={{ fontSize: '0.75rem' }}>{formatDate(at)}</span>
                </div>
            </div>
        </div>
    );
};

const PurchaseHistory = ({ purchases, isBAC, selectedIndex, onSelect, status }) => {
    // Newest first. The purchase the page is showing starts open — that is the
    // newest by default, or the one a Past Orders row linked to.
    const ordered = [...(purchases || [])].reverse();
    const initial = selectedIndex ?? (ordered.length ? ordered[0].index : null);
    const [expanded, setExpanded] = useState(initial);

    // Follow the page's selection when it changes (the selector, or a new link).
    const [lastSelected, setLastSelected] = useState(selectedIndex);
    if (selectedIndex !== lastSelected) {
        setLastSelected(selectedIndex);
        setExpanded(selectedIndex);
    }

    if (!ordered.length) {
        return (
            <div className="text-white-50 text-center py-4">
                <span className="fas fa-receipt me-2" />
                No purchases yet
            </div>
        );
    }

    const toggle = (index) => {
        const next = expanded === index ? null : index;
        setExpanded(next);
        // Opening a purchase here also points the panels above at it, so the
        // page never shows one purchase's terms next to another's events.
        if (next !== null) onSelect?.(index);
    };

    return (
        <div className="purchase-history">
            {ordered.map((purchase) => {
                const isExpanded = expanded === purchase.index;
                const priceChanged = purchase.requestedPrice != null
                    && purchase.price != null
                    && purchase.requestedPrice !== purchase.price;
                const dateChanged = purchase.requestedScreeningDate
                    && purchase.screeningDate
                    && new Date(purchase.requestedScreeningDate).getTime() !== new Date(purchase.screeningDate).getTime();

                return (
                    <div
                        key={purchase.index}
                        className={`purchase-block bg-broken mb-2 shadow-sm overflow-hidden${isExpanded ? ' selected' : ''}`}
                    >
                        <div
                            className="d-flex align-items-center gap-3 p-3 pointer"
                            onClick={() => toggle(purchase.index)}
                            style={{ userSelect: 'none' }}
                        >
                            <div className="purchase-number flex-shrink-0 d-flex align-items-center justify-content-center">
                                {purchase.index + 1}
                            </div>

                            <div className="flex-grow-1 min-w-0">
                                <div className="text-white fw-semibold">
                                    <span className="fas fa-calendar me-2 text-primary" />
                                    {formatDate(purchase.screeningDate)}
                                    {dateChanged && (
                                        <span className="text-white-50 ms-2" style={{ fontSize: '0.75rem' }}>
                                            requested {formatDate(purchase.requestedScreeningDate)}
                                        </span>
                                    )}
                                </div>
                                <div className="text-white-50 small">
                                    Purchased {formatDate(purchase.purchasedAt)}
                                    {purchase.endedAt && ` · ended ${formatDate(purchase.endedAt)}`}
                                </div>
                            </div>

                            <div className="text-primary fw-bold flex-shrink-0 d-none d-md-block">
                                <span className="fas fa-coins me-2" />
                                {priceChanged && (
                                    <span className="text-decoration-line-through text-white-50 me-2">
                                        {formatCredits(purchase.requestedPrice)}
                                    </span>
                                )}
                                {formatCredits(purchase.price)}
                            </div>

                            <OrderStatusBadge status={status} isCard className="flex-shrink-0" />

                            <span
                                className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-white-50 flex-shrink-0`}
                                style={{ fontSize: '0.75rem' }}
                            />
                        </div>

                        {isExpanded && (
                            <div className="px-3 pb-3">
                                <div className="bg-black/50 p-3 rounded">
                                    <div className="row g-3">
                                        <div className="col-md-4">
                                            <div className="text-white-50 small mb-1">Hire period</div>
                                            <div className="text-white">
                                                <span className="fas fa-clock me-2 text-primary" />
                                                {purchase.hirePeriod
                                                    ? `${purchase.hirePeriod} ${purchase.hirePeriodType || ''}`.trim()
                                                    : '—'}
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-white-50 small mb-1">Paid</div>
                                            <div className="text-primary">
                                                <span className="fas fa-coins me-2" />
                                                {formatCredits(purchase.price)} credits
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="text-white-50 small mb-1">Status</div>
                                            <OrderStatusBadge status={status} isCard className="d-inline-flex" />
                                        </div>
                                    </div>

                                    <div className="d-flex flex-wrap gap-4 mt-3">
                                        <Person label="Requested by" user={purchase.purchasedBy} at={purchase.purchasedAt} />
                                        <Person label="Terms set by" user={purchase.approvedBy} at={purchase.approvedAt} />
                                        <Person label="Accepted by" user={purchase.acceptedBy} at={purchase.acceptedAt} />
                                        {purchase.outcome !== 'active' && (
                                            <Person label="Closed by" user={purchase.closedBy} at={purchase.endedAt} />
                                        )}
                                    </div>

                                    {purchase.terms && (
                                        <div className="mt-3">
                                            <div className="text-white-50 small mb-1">Screening terms</div>
                                            <div className="text-white fst-italic">
                                                <span className="fas fa-quote-left me-2 text-primary" />
                                                {purchase.terms}
                                            </div>
                                        </div>
                                    )}

                                    {purchase.reason && (
                                        <div className="mt-3">
                                            <div className="text-white-50 small mb-1">Reason</div>
                                            <div className="text-white small">{purchase.reason}</div>
                                        </div>
                                    )}
                                </div>

                                {purchase.events?.length > 0 && (
                                    <Fragment>
                                        <div className="text-white-50 small mt-3 mb-2">
                                            <span className="fas fa-list-ul me-2" />
                                            Events
                                        </div>
                                        <OrderHistory orderHistory={purchase.events} isBAC={isBAC} />
                                    </Fragment>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default PurchaseHistory;
